import React from 'react';
import styled, { css } from 'styled-components';
import { space, SpaceProps } from 'styled-system';

import Drawer from '@material-ui/core/Drawer';
import { ModalProps } from '@material-ui/core';

import { ModalExitIcon } from 'shared/modals/v2-container';
import { heading3 } from 'components/core/typography';
import { BackButton } from './back-button';

export type BottomDrawerProps = {
  backButtonCopy?: string;
  handleBackButtonClick?: () => void;
  children: React.ReactNode;
  open: boolean;
  onClose?: () => void;
  heading?: string;
  subheading?: string;
  hideExitIcon?: boolean;
  fullHeight?: boolean;
  shouldCalculateIframeBottom?: boolean;
  shouldUseImportantZIndex?: boolean;
  shouldIncludePaperPadding?: boolean;
  iframeHeight?: number | null;
  viewportHeight?: number | null;
  parentOffset?: number | null;
  iframeViewport?: { top: number; height: number };
  isIframe?: boolean;
  ModalProps?: Partial<ModalProps>;
};

export function BottomDrawer({
  backButtonCopy,
  handleBackButtonClick,
  children,
  onClose,
  heading,
  subheading,
  hideExitIcon,
  fullHeight,
  shouldCalculateIframeBottom = false,
  shouldUseImportantZIndex = true,
  shouldIncludePaperPadding = true,
  iframeHeight = null,
  viewportHeight = null,
  parentOffset = null,
  isIframe = false,
  iframeViewport,
  ...props
}: BottomDrawerProps): JSX.Element {
  const positionedViewport =
    shouldCalculateIframeBottom &&
    isIframe &&
    typeof iframeHeight === 'number' &&
    Number.isFinite(iframeHeight) &&
    iframeHeight >= 0 &&
    iframeViewport &&
    Number.isFinite(iframeViewport.top) &&
    iframeViewport.top >= 0 &&
    Number.isFinite(iframeViewport.height) &&
    iframeViewport.height >= 0
      ? iframeViewport
      : undefined;
  const legacyBottomPosition =
    shouldCalculateIframeBottom && isIframe && iframeHeight && viewportHeight && parentOffset !== null
      ? Math.max(iframeHeight - viewportHeight - parentOffset, 0)
      : null;
  const bottomPosition = positionedViewport
    ? Math.max((iframeHeight ?? 0) - positionedViewport.top - positionedViewport.height, 0)
    : legacyBottomPosition;

  const content = (
    <>
      {heading && <Heading>{heading}</Heading>}
      {subheading && <Subheading>{subheading}</Subheading>}
      {backButtonCopy && handleBackButtonClick && <BackButton copy={backButtonCopy} onClick={handleBackButtonClick} />}
      {!hideExitIcon && !!onClose && <StyledExitIcon onClick={onClose} />}
      {children}
    </>
  );

  return (
    <StyledDrawer
      anchor='bottom'
      onClose={onClose}
      $bottomPosition={bottomPosition}
      $availableHeight={positionedViewport?.height}
      $fullHeight={!!fullHeight}
      $shouldUseImportantZIndex={shouldUseImportantZIndex}
      $shouldIncludePaperPadding={shouldIncludePaperPadding}
      {...props}
    >
      {positionedViewport ? (
        <BoundedContent $shouldIncludePaperPadding={shouldIncludePaperPadding}>{content}</BoundedContent>
      ) : (
        content
      )}
    </StyledDrawer>
  );
}

type StyledDrawerProps = {
  isBackgroundVisible?: boolean;
  $bottomPosition?: number | null;
  $availableHeight?: number;
  $fullHeight?: boolean;
  $shouldUseImportantZIndex?: boolean;
  $shouldIncludePaperPadding?: boolean;
};

const StyledDrawer = styled(Drawer)<StyledDrawerProps>`
  /* Root: optional important z-index to override mui inline styles */
  ${({ $shouldUseImportantZIndex }) =>
    $shouldUseImportantZIndex &&
    css`
      &.MuiDrawer-root {
        z-index: 1400 !important;
      }
    `}

  .MuiBackdrop-root {
    background-color: ${({ theme, isBackgroundVisible }) => !isBackgroundVisible && theme.colors.greyOverlay};
  }

  > .MuiPaper-root.MuiDrawer-paper {
    z-index: 1500;
    background-color: var(--dcm-bottom-drawer-bg, ${({ theme }) => theme.colors.white});
    color: var(--dcm-bottom-drawer-text-color, rgba(0, 0, 0, 0.87));
    .concierge-mode & {
      --dcm-bottom-drawer-text-color: var(--dcm-modal-text-color);
    }
    ${({ $shouldIncludePaperPadding }) =>
      $shouldIncludePaperPadding &&
      css`
        padding: 25px 25px 50px;
      `}
    ${({ $fullHeight }) =>
      $fullHeight
        ? css`
            height: 100%;
          `
        : css`
            border-radius: 23px 23px 0px 0px;
          `}

    /* apply bottom padding only on iOS since Safari's bottom menu bar was interfering w buttons */
    ${({ $availableHeight }) =>
      $availableHeight === undefined &&
      css`
        @supports (-webkit-touch-callout: none) {
          padding-bottom: calc(49px + env(safe-area-inset-bottom));
        }
      `}

    ${({ $availableHeight }) =>
      $availableHeight !== undefined &&
      css`
        box-sizing: border-box;
        height: auto;
        min-height: 0;
        padding: 0;
        max-height: ${$availableHeight}px;
        overflow-y: auto;
      `}

    /* Fix for positioning in iframe */
    ${({ $bottomPosition }) =>
      $bottomPosition !== null &&
      css`
        bottom: ${$bottomPosition}px !important;
      `}
  }
`;

const StyledExitIcon = styled(ModalExitIcon)`
  top: 25px;
  right: 23px;
  svg {
    fill: var(--dcm-bottom-drawer-exit-color, ${({ theme }) => theme.colors.grey[35]});
  }
`;

const Heading = styled.h3`
  ${heading3}
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
`;

const Subheading = styled.p`
  color: var(--dcm-color-text-secondary, ${({ theme }) => theme.colors.grey[45]});
  font-size: 12px;
  margin-top: 8px;
`;

type ElevatedSectionProps = SpaceProps & {
  borderPosition: 'bottom' | 'top';
};

export const ElevatedSection = styled.div<ElevatedSectionProps>`
  ${({ theme, borderPosition = `bottom` }) =>
    borderPosition === 'top'
      ? css`
          border-top: 1px solid ${theme.colors.grey[90]};
          box-shadow: 0px -3px 1px ${theme.colors.sectionShadow};
        `
      : css`
          border-bottom: 1px solid ${theme.colors.grey[90]};
          box-shadow: 0px 3px 1px ${theme.colors.sectionShadow};
        `}
  margin: 0 -25px;
  padding: 0 25px;
  ${space}
`;

// Avoid renumbering existing styled components imported through the core barrel.
const BoundedContent = styled.div.withConfig({ componentId: 'sc-bottom-drawer-bounded-content' })<{
  $shouldIncludePaperPadding: boolean;
}>`
  padding: ${({ $shouldIncludePaperPadding }) => ($shouldIncludePaperPadding ? '25px 25px 50px' : '0')};

  @supports (-webkit-touch-callout: none) {
    padding-bottom: calc(49px + env(safe-area-inset-bottom));
  }
`;
